import setIfExists from 'utils/set-if-exists.js';
import moment from 'moment';

angular
  .module('inkfinder')
  .factory('Association', function Association(
    $rootScope,
    $http,
    $q,
    ENDPOINT,
    $uibModal,
    AlertMessages,
    Session,
    _
  ) {
    // Public Methods
    Association.parseUserAssociations = parseUserAssociations;
    Association.createAssociation = createAssociation;
    Association.deleteAssociation = deleteAssociation;
    Association.getAssociations = getAssociations;
    Association.getAssociation = getAssociation;
    Association.getShopAssociations = getShopAssociations;
    Association.determineAssociationConflict = determineAssociationConflict;
    Association.sortByAssociations = sortByAssociations;

    // Convienence Functions
    Association.declineByShop = declineByShop;
    Association.declineByUser = declineByUser;
    Association.accept = accept;
    Association.expire = expire;
    Association.update = updateAssociation;

    // Clear data
    let deregister = $rootScope.$on('clear-data', function() {
      Association.associations = {};
      Association.association = {};
    });
    $rootScope.$on('$destroy', deregister);

    /**
  * Get all associations for the logged in user
  * @return {promise} Returns associations object
  * TODO Review if this function is needed now.
  */
    function getAssociations() {
      return $http
        .get(ENDPOINT.server + 'collectors/api/v1.1/association/')
        .then(
          function(response) {
            let associations = response.data;
            Association.associations = parseUserAssociations(associations);
          },
          function(error) {
            AlertMessages.add({
              msg: error,
              type: 'danger'
            });
          }
        );
    }

    /**
   * @name getShopAssociations
   * @description Get all associations for a shop
   * @param {shopId} shopId The ID of the shop to retrieve their associations
   * @param {string} role The role of the logged in user
   * @param {string} type The type of associations to return, ie, operator, artist, or collector
   * @param {number} page Pagination support
   * @param {string} search Text search support
   * @param {number} appointmentWith Filter to limit assocaitions to ones that have an appointment with
   * a particular artist
   * @return {promise} Returns a promise containing shop associations
   */
    function getShopAssociations(shopId, role, type, page, search, appointmentWith) {
      if (_.isUndefined(shopId)) {
        shopId = _.isUndefined(Session.activeState.association)
          ? ''
          : Session.activeState.association.shop.id;
      }
      if (_.isUndefined(role) || Session.activeState === null) {
        role = '';
      }
      return $http
        .get(
          ENDPOINT.server +
            'collectors/api/v1.1/association/?' +
            (shopId ? 'shop_id=' + shopId : '') +
            (type ? '&type=' + type : '') +
            (role ? '&role=' + role : '') +
            (page ? '&page=' + page : '') +
            (search ? '&search=' + search : '') +
            (appointmentWith ? '&appointment-with=' + appointmentWith : '')
        )
        .then(
          function(response) {
            let associations;
            if (page) {
              response.data.associations = parseCollectorAssociations(response.data.results);
              return response.data;
            }
            associations = parseCollectorAssociations(response.data);
            Association.associations = associations;
            return associations;
          },
          function(error) {
            AlertMessages.add({
              msg: error,
              type: 'danger'
            });
          }
        );
    }

    /**
   * @name getAssociation
   * @description Get details for a single association based on the collector ID
   * @param {array} collectorIds The ID of the collector to retrieve their
   * association
   * @param {string} role The role of the activeState of the logged in user
   * association
   * @return {object} Returns an association object
   */
    function getAssociation(collectorIds) {
      let url = ENDPOINT.server + 'collectors/api/v1.1/association/?';
      if (!_.isUndefined(collectorIds)) {
        url += 'collector_ids=' + collectorIds;
      }
      if (Session.activeState) {
        if (
          Session.activeState.role === 'operator' ||
          Session.activeState.role === 'artist'
        ) {
          let role = Session.activeState.role;
          url += '&role=' + role;
        }
      }
      return $http.get(url).then(
        function(associations) {
          let parsedAssociations = parseUserAssociations(associations.data);
          return parsedAssociations;
        },
        function(error) {
          AlertMessages.add({
            msg: error,
            type: 'danger'
          });
        }
      );
    }

    /**
  * Update a single association based on the association ID
  * @param {association} association The assoication to be updated
  * @return {object} Returns a promise object containing the updated association
  */
    function updateAssociation(association) {
      return $http
        .put(
          ENDPOINT.server +
            'collectors/api/v1.1/association/' +
            association.id +
            '/',
          association
        )
        .then(
          function(response) {
            let association = response.data;
            return association;
          },
          function(error) {
            AlertMessages.add({
              msg: error,
              type: 'danger'
            });
          }
        );
    }

    /**
  * Create an association
  * @param {type} type The type of association to create. Options accepted are
  * 'artist' and 'operator'.
  * @param {association} association The formatted association data
  * @return {object} Returns an association object
  */
    function createAssociation(type, association) {
      let _association = association;
      _association.type = type;
      let _formattedAssocation = formatAssociation(association);
      return $http
        .post(
          ENDPOINT.server + 'collectors/api/v1.1/association/',
          _formattedAssocation
        )
        .then(
          function(response) {
            return response.data;
          },
          function(error) {
            AlertMessages.add({
              msg: error,
              type: 'danger'
            });
          }
        );
    }

    /**
  * Delete an association
  * @param {association} association The association to be deleted
  */
    function deleteAssociation(association) {
      $http
        .delete(
          ENDPOINT.server +
            'collectors/api/v1.1/association/' +
            association.id +
            '/'
        )
        .then(
          function(response) {
            return response.data;
          },
          function(error) {
            AlertMessages.add({
              msg: error.data,
              type: 'danger'
            });
          }
        );
    }

    /**
  * Update an association to shop declined
  * @param {input} input The association object to be changed
  * @return {object} Returns the updated association
  */
    function declineByShop(input) {
      input.status = 'Shop Declined';
      let association = formatAssociation(input);
      return updateAssociation(association);
    }

    /**
  * Update an association to user declined
  * @param {input} input The association object to be changed
  * @return {object} Returns the updated association
  */
    function declineByUser(input) {
      input.status = 'User Declined';
      let association = formatAssociation(input);
      return updateAssociation(association);
    }

    /**
  * Update an association to confirmed
  * @param {input} input The association object to be changed
  * @return {object} Returns the updated association
  */
    function accept(input) {
      input.status = 'Confirmed';
      let association = formatAssociation(input);
      return updateAssociation(association);
    }

    /**
  * Update an association to expired (will set expiration date to today)
  * @param {input} association The association object to be changed
  * @return {object} Returns the updated association
  */
    function expire(association) {
      association.status = 'Expired';
      association.endDatetime = moment().format();
      return updateAssociation(association);
    }

    /**
  * Formats Association Data
  * @param {input} input The association to format
  * @return {object} Returns a formatted association object
  */
    function formatAssociation(input) {
      let _association = {};
      if (!setIfExists(input, 'status', _association)) {
        _association.status = input.status;
      }
      if (!setIfExists(input, 'type', _association)) {
        _association.type = input.type;
      }
      if (!setIfExists(input, 'startDatetime', _association)) {
        _association.startDatetime = moment().format();
      }
      if (input.endDatetime) {
        _association.endDatetime = input.endDateTime;
      }
      if (input.operatorAdmin) {
        _association.operatorAdmin = input.operatorAdmin;
      }
      if (input.operatorOwner) {
        _association.operatorOwner = input.operatorOwner;
      }
      setIfExists(input, 'shop_id', _association);
      setIfExists(input, 'collector_id', _association);
      setIfExists(input, 'id', _association);
      setIfExists(input, 'can_update_appts', _association);
      return _association;
    }

    /** Parse user Associations (used for logged in user)
  * @param {associations} associations The object of associations to parse
  * @return {object} Returns an object of parsed associations
  */
    function parseUserAssociations(associations) {
      let sorted = {artist: [], operator: [], collector: []};
      let processed = {};

      for (let i = 0; i < associations.length; i++) {
        delete associations[i].collector; // Delete the Collector (not needed)
        associations[i].type = associations[i].type.toLowerCase();
        if (associations[i].type === 'operator') {
          sorted.operator.push(associations[i]);
        } else if (associations[i].type === 'artist') {
          sorted.artist.push(associations[i]);
        } else {
          sorted.collector.push(associations[i]);
        }
      }
      processed.all = associations;
      processed.operators = sortByAssociations(sorted.operator);
      processed.artists = sortByAssociations(sorted.artist);
      processed.collectors = sortByAssociations(sorted.collector);
      return processed;
    }

    /**
  * Parse the Associations into Collector Prioritized Objects (collector oriented)
  * @param {associations} associations The associations of the collectors to parse
  * @return {object} Calls sortCollectors and returns an object of collectors
  */
    function parseCollectorAssociations(associations) {
      let collectors = [];
      for (let i = 0; i < associations.length; i++) {
        let association = associations[i];
        let collector = association.collector;
        collector.association = association;
        collector.association.type = collector.association.type.toLowerCase();
        delete collector.association.collector; // Delete Extra Collector Data
        collectors.push(collector);
      }
      return sortCollectors(collectors);
    }

    /**
  * Sort the Collectors into type based on their Association
  * @param {collectors} collectors The object of collectors to sort
  * @return {object} Returns an object of sorted collectors
  */
    function sortCollectors(collectors) {
      let sorted = {artist: [], operator: [], collector: []};
      let processed = {};

      for (let i = 0; i < collectors.length; i++) {
        sorted[collectors[i].association.type].push(collectors[i]);
      }

      // Assign the Data
      processed.all = collectors;
      processed.artists = sortByAssociations(sorted.artist);
      processed.operators = sortByAssociations(sorted.operator);
      processed.collectors = sortByAssociations(sorted.collector);

      return processed;
    }

    /**
  * Sort by Association Type (abstracted to take collectors [w/ association] and associations).
  * @param {inputArray} inputArray The array of associations to sort
  * @return {object} Returns an object of arrays. Arrays are sorted by association status.
  */
    function sortByAssociations(inputArray) {
      let processed = {
        confirmed: [],
        invited: [],
        declined: [],
        expired: [],
        valid: [],
        inactive: []
      };

      // Loop through Associations
      for (let i = 0; i < inputArray.length; i++) {
        let item = inputArray[i];

        // Collector Object (w/ association object) or Association Object
        let association;
        if (item.association) {
          association = item.association;
        } else {
          association = item;
        }

        let status = association.status;
        let expiration = association.endDatetime;

        // Generate a list of Valid Associations
        if (status === 'Confirmed' || status === 'Expired') {
          processed.valid.push(item);
        }

        if (expiration && moment(expiration) < moment()) {
          if (status === 'Confirmed') {
            processed.inactive.push(item);
          } else {
            processed.expired.push(item);
          }
        } else if (status === 'Confirmed') {
          processed.confirmed.push(item);
        } else if (status === 'Shop Invited' || status === 'User Invited') {
          processed.invited.push(item);
        } else if (status === 'User Declined' || status === 'Shop Declined') {
          processed.declined.push(item);
        }
      }

      // Everything
      processed.all = inputArray;
      return processed;
    }

    /**
  * Check for conflicts with associations
  * @param {collectors} collectors The collectors to check for conflicts
  * @param {group} group The group to check within?
  */
    function determineAssociationConflict(collectors, group) {
      let conflict = [];

      for (let i = 0; i < group.invited.length; i++) {
        conflict[group.invited[i].id] = group.invited[i];
      }

      for (let d = 0; d < group.confirmed.length; d++) {
        conflict[group.confirmed[d].id] = group.confirmed[d];
      }

      for (let s = 0; s < collectors.length; s++) {
        if (conflict[collectors[s].id]) {
          collectors[s].associationConflict = true;
        } else {
          collectors[s].associationConflict = false;
        }
      }
    }
    return Association;
  });



// WEBPACK FOOTER //
// ./components/collector/associations/association.service.js